import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Avatar,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import {
  LocalShipping,
  Schedule,
  Visibility,
  AssignmentTurnedIn,
  Inventory2,
  MoveToInbox,
  TaskAlt,
  Download,
  ReceiptLongOutlined,
  ShoppingBagOutlined
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import ReviewModal from "../../Components/ReviewModal/ReviewModal";
import { postData, postFileUpload } from "../../Webservice/Webservice";
import { Alert } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { API_URL } from "../../Utils/Url";

function MyOrders() {
  const navigate = useNavigate();
  const [reviewModal, setReviewModal] = useState({
    open: false,
    orderId: null,
    productId: null,
    productName: "",
    productImage: "",
    rating: 0,
    review: ""
  });
  const [returnModal, setReturnModal] = useState({
    open: false,
    orderId: null,
    productId: null,
    reason: "",
    video: null,
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const tokenValue = localStorage.getItem("website_jwt_token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      if (!token) {
        setError("Please login to view your orders");
        setLoading(false);
        return;
      }

      const response = await postData(
        "order/order-list",
        { "appStatus": selectedStatus ? [selectedStatus] : [""] },
        token,
        false
      );

      if (response && response.orders) {
        // Debug: Log first order to check structure
        if (response.orders.length > 0) {
          console.log("Sample order from API:", response.orders[0]);
        }

        // Transform API response to match component structure
        const transformedOrders = response.orders.map((order) => {
          // Calculate total from items
          const itemsTotal = order.items.reduce((sum, item) => sum + (item.total || 0), 0);
          const finalOrderTotal = order.totalAmount !== undefined 
            ? order.totalAmount 
            : (order.orderValue !== undefined ? order.orderValue : (order.total || itemsTotal));

          // Transform items
          const transformedItems = order.items.map((item) => {
            const isCombo = !!item.isCombo;
            const comboDetails = item.comboDetails || null;
            return {
              id: item.product_id?._id,
              name: item.product_name || (isCombo && comboDetails?.title) || item.product_id?.title || "Product",
              image: (isCombo && comboDetails?.image) || item.product_id?.product_images?.[0] || "",
              price: item.price || 0,
              quantity: item.quantity || 1,
              isFree: !!item.isFree || item.price === 0,
              isCombo,
              review: item.review || null,
              returnIsvalid: item.returnIsvalid || false
            };
          });

          // Format date
          const orderDate = new Date(order.createdAt);
          const formattedDate = orderDate.toLocaleDateString("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });

          // Ensure _id exists
          if (!order._id) {
            console.warn("Order _id is missing:", order);
          }

          return {
            id: order.orderNumber ? `#${order.orderNumber}` : `#${(order._id || order.id || "").toString().slice(-8).toUpperCase()}`,
            _id: order._id || order.id, // Store the MongoDB _id for API calls, fallback to id
            date: formattedDate,
            status: order.appStatus || "confirmed",
            items: transformedItems,
            total: Number(finalOrderTotal || 0).toFixed(2),
            tracking: order.orderNumber || order._id,
            invoiceUrl: order.customerInvoice || null,
            invoiceNumber: order.invoiceNumber,
          };
        });

        setOrders(transformedOrders);
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message || "Failed to fetch orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, selectedStatus]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed":
        return <AssignmentTurnedIn sx={{ color: "white" }} />; // Blue - confirmed

      case "packed":
        return <Inventory2 sx={{ color: "white" }} />; // Purple - packed

      case "shipped":
        return <LocalShipping sx={{ color: "white" }} />; // Teal - shipped

      case "out-for-delivery":
        return <MoveToInbox sx={{ color: "white" }} />; // Yellow - out for delivery

      case "delivered":
        return <TaskAlt sx={{ color: "white" }} />; // Green - delivered

      default:
        return <Schedule sx={{ color: "white" }} />; // Default
    }
  };

  const handleFilterMenuOpen = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    handleFilterMenuClose();
  };

  const handleOpenReview = (orderId, product) => {
    setReviewModal({
      open: true,
      orderId,
      productId: product.id,
      productName: product.name,
      productImage: product.image,
      rating: product.review?.rating || 0,
      review: product.review?.review || ""
    });
  };

  const handleCloseReview = () => {
    setReviewModal({
      open: false,
      orderId: null,
      productId: null,
      productName: "",
      productImage: "",
      rating: 0,
      review: ""
    });
  };

  const handleSubmitReview = async (reviewData) => {
    try {
      const tokenValue = localStorage.getItem("website_jwt_token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      const res = await postData("rating/add", {
        orderId: reviewModal.orderId,
        productId: reviewModal.productId,
        ...reviewData
      }, token);

      if (res) {
        fetchOrders(); // Refresh to show the new rating
      }
    } catch (error) {
      console.error("Error submitting review:", error);
    }
  };
  const handleOpenReturnModal = (orderId, productId) => {
    setReturnModal({ open: true, orderId, productId, reason: "", video: null });
  };

  const handleCloseReturnModal = () => {
    setReturnModal({ open: false, orderId: null, productId: null, reason: "", video: null });
  };

  const handleConfirmReturn = async () => {
    try {
      if (!returnModal.reason.trim() || !returnModal.video) return;

      const tokenValue = localStorage.getItem("website_jwt_token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) {
        setError("Please login to return orders");
        return;
      }

      const formData = new FormData();
      formData.append("productId", returnModal.productId);
      formData.append("returnReason", returnModal.reason);
      formData.append("return_video", returnModal.video);

      await postFileUpload(`order/return/${returnModal.orderId}`, formData, token);

      setReturnModal({ open: false, orderId: null, productId: null, reason: "", video: null });
      fetchOrders();
    } catch (err) {
      console.error("Failed to return order:", err);
    }
  };

  const handleDownloadInvoice = async (invoiceNumber, orderId) => {
    try {
      if (!invoiceNumber) {
        setError("Invoice number not found for this order.");
        return;
      }
      setDownloadingId(orderId);
      const tokenValue = localStorage.getItem("website_jwt_token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      const response = await fetch(`${API_URL}order/download-invoice/${invoiceNumber}`, {
        headers: {
          'Authorization': token
        }
      });
      if (!response.ok) {
        throw new Error("Failed to download invoice");
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error("Failed to download invoice:", err);
      setError("Unable to download invoice at this time.");
    } finally {
      setDownloadingId(null);
    }
  };

  // Skeleton component for order items
  const OrderSkeleton = () => (
    <Paper
      elevation={1}
      sx={{
        bgcolor: "white",
        overflow: "hidden",
        boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        borderRadius: "20px"
      }}
    >
      {/* Order Header Skeleton */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container alignItems="center" sx={{ justifyContent: "space-between" }} spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Skeleton variant="circular" width={42} height={42} />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="80%" height={16} sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: "20px" }} />
              <Skeleton variant="text" width={60} height={20} />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      {/* Order Items Skeleton */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1.5 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* First item skeleton */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              borderRadius: "10px",
              border: "1px solid #f0f0f0",
              flexDirection: { xs: "row", sm: "row" },
            }}
          >
            <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: "8px", flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="70%" height={16} />
              <Skeleton variant="text" width="40%" height={14} sx={{ mt: 0.5 }} />
            </Box>
            <Skeleton variant="text" width={50} height={16} />
          </Box>
          {/* Second item skeleton */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              borderRadius: "10px",
              border: "1px solid #f0f0f0",
              flexDirection: { xs: "row", sm: "row" },
            }}
          >
            <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: "8px", flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="60%" height={16} />
              <Skeleton variant="text" width="35%" height={14} sx={{ mt: 0.5 }} />
            </Box>
            <Skeleton variant="text" width={45} height={16} />
          </Box>
        </Box>
      </Box>
      {/* Footer Skeleton */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: "24px" }} />
          <Skeleton variant="rectangular" width={140} height={32} sx={{ borderRadius: "24px" }} />
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 3, md: 6 },
        color: "black",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: { xs: 3, md: 5 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "26px", sm: "32px", md: "40px" },
            fontWeight: 500,
            color: "#111",
          }}
        >
          My Orders
        </Typography>

        <Box>
          <IconButton
            aria-label="more options"
            onClick={handleFilterMenuOpen}
            sx={{ color: "#111" }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={filterMenuAnchor}
            open={Boolean(filterMenuAnchor)}
            onClose={handleFilterMenuClose}
            PaperProps={{
              sx: {
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                borderRadius: "12px",
              },
            }}
          >
            <MenuItem
              onClick={() => handleStatusSelect("")}
              selected={selectedStatus === ""}
              sx={{
                fontWeight: selectedStatus === "" ? 600 : 400,
                color: selectedStatus === "" ? "black" : "inherit"
              }}
            >
              All Orders
            </MenuItem>
            <MenuItem
              onClick={() => handleStatusSelect("confirmed")}
              selected={selectedStatus === "confirmed"}
              sx={{
                fontWeight: selectedStatus === "confirmed" ? 600 : 400,
                color: selectedStatus === "confirmed" ? "black" : "inherit"
              }}
            >
              Confirmed
            </MenuItem>
            <MenuItem
              onClick={() => handleStatusSelect("delivered")}
              selected={selectedStatus === "delivered"}
              sx={{
                fontWeight: selectedStatus === "delivered" ? 600 : 400,
                color: selectedStatus === "delivered" ? "black" : "inherit"
              }}
            >
              Delivered
            </MenuItem>
            <MenuItem
              onClick={() => handleStatusSelect("cancelled")}
              selected={selectedStatus === "cancelled"}
              sx={{
                fontWeight: selectedStatus === "cancelled" ? 600 : 400,
                color: selectedStatus === "cancelled" ? "black" : "inherit"
              }}
            >
              Cancelled
            </MenuItem>
            <MenuItem
              onClick={() => handleStatusSelect("return")}
              selected={selectedStatus === "return"}
              sx={{
                fontWeight: selectedStatus === "return" ? 600 : 400,
                color: selectedStatus === "return" ? "black" : "inherit"
              }}
            >
              Return
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Loading State - Show skeleton loaders */}
      {loading && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <OrderSkeleton />
          <OrderSkeleton />
          <OrderSkeleton />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Orders List */}
      {!loading && !error && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {orders.length === 0 ? (
            // empty state: a fixed-size, centred card with a clear next step
            <Box
              sx={{
                width: "100%",
                maxWidth: 520,
                minHeight: 340,
                mx: "auto",
                my: { xs: 2, md: 4 },
                px: { xs: 3, sm: 5 },
                py: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                borderRadius: "24px",
                border: "1px solid #efe6d0",
                background: "linear-gradient(180deg, #fffdf8 0%, #fbf5e6 100%)",
                boxShadow: "0 8px 28px rgba(60,40,10,0.07)",
              }}
            >
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#fbf0d6",
                  color: "#C8A054",
                  mb: 2.5,
                  boxShadow: "0 0 0 10px rgba(200,160,84,0.12)",
                }}
              >
                <ReceiptLongOutlined sx={{ fontSize: 46 }} />
              </Box>

              <Typography sx={{ fontSize: { xs: "20px", sm: "22px" }, fontWeight: 600, color: "#111" }}>
                {selectedStatus ? `No ${selectedStatus.replace(/_/g, " ")} orders` : "No orders yet"}
              </Typography>
              <Typography sx={{ mt: 1, mb: 3, maxWidth: 360, fontSize: "14.5px", lineHeight: 1.6, color: "text.secondary" }}>
                {selectedStatus
                  ? "None of your orders match this filter. Try another status or view all your orders."
                  : "When you place an order, it will show up here so you can track it, download the invoice or return it."}
              </Typography>

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<ShoppingBagOutlined />}
                  onClick={() => navigate("/products")}
                  sx={{ px: 3, py: 1, borderRadius: "12px", textTransform: "none", fontWeight: 600, bgcolor: "#C8A054", "&:hover": { bgcolor: "#b38841" } }}
                >
                  Start shopping
                </Button>
                {selectedStatus && (
                  <Button
                    variant="outlined"
                    onClick={() => handleStatusSelect("")}
                    sx={{ px: 3, py: 1, borderRadius: "12px", textTransform: "none", fontWeight: 600, color: "#8a6a24", borderColor: "rgba(200,160,84,0.6)", "&:hover": { borderColor: "#C8A054", bgcolor: "#fbf3df" } }}
                  >
                    Show all orders
                  </Button>
                )}
              </Box>
            </Box>
          ) : (
            orders.map((order) => (
              <Paper
                key={order._id || order.id}
                elevation={1}
                sx={{
                  // border: "1px solid black",
                  bgcolor: "white",
                  overflow: "hidden",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                  borderRadius: "20px"
                }}
              >
                {/* Order Header */}
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <Grid container alignItems="center" sx={{ justifyContent: "space-between" }} spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: "#C8A054",
                            width: 42,
                            height: 42,
                          }}
                        >
                          {getStatusIcon(order.status)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                          >
                            {order.id}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", fontSize: "13px" }}
                          >
                            Ordered on {order.date}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: {
                            xs: "flex-start",
                            sm: "flex-end",
                          },
                          alignItems: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={order.status.toUpperCase()}
                          sx={{
                            fontWeight: 500,
                            color: "white",
                            bgcolor: "#C8A054",
                            borderRadius: "20px",
                            fontSize: "12px",
                            px: 1.5,
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{ fontSize: "15px" }}
                        >
                          ₹{order.total}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Divider />
                {/* Order Items */}
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 1.5, color: "#111" }}
                  >
                    Order Items
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {order.items.map((item, idx) => (
                      <Box
                        key={item.id || idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1.5,
                          borderRadius: "10px",
                          border: "1px solid #f0f0f0",
                          flexDirection: { xs: "row", sm: "row" },
                        }}
                      >
                        <Avatar
                          src={item.image}
                          variant="rounded"
                          sx={{
                            width: 64,
                            height: 64,
                            border: "1px solid #ddd",
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              sx={{ fontSize: "14px" }}
                            >
                              {item.name}
                            </Typography>
                            {item.isCombo && (
                              <Chip
                                label="🎁 Combo"
                                size="small"
                                sx={{
                                  fontSize: "10px",
                                  height: "18px",
                                  fontWeight: 700,
                                  bgcolor: "#fff3e0",
                                  color: "#e65100",
                                  border: "1px solid #ffcc80"
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "13px" }}
                          >
                            Qty: {item.quantity}
                          </Typography>

                          {/* Showing Rating if it exists */}
                          {item.review && (
                            <Box sx={{ mt: 0.5 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Rating
                                  value={item.review.rating}
                                  readOnly
                                  size="small"
                                  sx={{ fontSize: "14px", color: "#C8A054" }}
                                />
                                <Typography sx={{ fontSize: "12px", color: "#C8A054", fontWeight: 700, ml: 0.5 }}>
                                  {item.review.rating}/5
                                </Typography>
                              </Box>
                              <Typography sx={{ fontSize: "11px", color: "#999", fontStyle: "italic", mt: 0.2 }}>
                                "{item.review.review}"
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>                          {/* Return & Review Buttons for Delivered items only */}
                          {order.status === "delivered" && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {item.returnIsvalid && (
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenReturnModal(order._id, item.id)}
                                  sx={{ textTransform: "none", fontSize: "11px", p: 0, minWidth: 0, fontWeight: 700, whiteSpace: "nowrap" }}
                                >
                                  Return Product
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="primary"
                                onClick={() => handleOpenReview(order._id, item)}
                                sx={{ textTransform: "none", fontSize: "11px", p: 0, minWidth: 0, fontWeight: 700, whiteSpace: "nowrap" }}
                              >
                                {item.review ? "Edit Review" : "Rate & Review"}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
                {/* Footer */}
                {/* <Divider /> */}
                <Box
                  sx={{
                    p: { xs: 2, sm: 3 },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      size="small"
                      disabled={!order._id}
                      onClick={() => {
                        if (order._id) {
                          navigate(`/orderdetails/${order._id}`);
                        } else {
                          console.error("Order ID is missing. Order data:", order);
                        }
                      }}
                      sx={{
                        color: "#C8A054",
                        borderColor: "#C8A054",
                        borderRadius: "24px",
                        textTransform: "none",
                        px: 2.5,
                        "&:hover": { bgcolor: "#C8A054", color: "white", borderColor: "#C8A054" },
                        "&:disabled": {
                          borderColor: "#ccc",
                          color: "#ccc",
                        },
                      }}
                    >
                      View Details
                    </Button>
                    
                    {order.status?.toLowerCase() === "delivered" && (
                      <Button
                        variant="contained"
                        startIcon={<Download />}
                        size="small"
                        disabled={downloadingId === (order._id || order.id)}
                        onClick={() => handleDownloadInvoice(order.invoiceNumber, order._id || order.id)}
                        sx={{
                          backgroundColor: "#C8A054",
                          color: "white",
                          borderRadius: "24px",
                          textTransform: "none",
                          px: 2.5,
                          boxShadow: "none",
                          "&:hover": { backgroundColor: "#b38841", boxShadow: "none" },
                          "&:disabled": { backgroundColor: "#e0e0e0" }
                        }}
                      >
                        {downloadingId === (order._id || order.id) ? "Downloading..." : "Download Invoice"}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            ))
          )}
        </Box>
      )}
      {/* Return Dialog */}
      <Dialog
        open={returnModal.open}
        onClose={handleCloseReturnModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: { xs: "16px", sm: "18px" } }}>
          Return Order
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            autoFocus
            margin="dense"
            label="Return Reason"
            fullWidth
            value={returnModal.reason}
            onChange={(e) =>
              setReturnModal((prev) => ({ ...prev, reason: e.target.value }))
            }
            InputLabelProps={{
              sx: { fontSize: { xs: "13px", sm: "14px" } },
            }}
            inputProps={{
              style: { fontSize: 13 },
            }}
            sx={{ mb: 2 }}
          >
            {[
              { value: "damaged", label: "Damaged" },
              { value: "wrong_item", label: "Wrong Item" },
              { value: "quality_issue", label: "Quality Issue" },
              { value: "other", label: "Other" },
            ].map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: 16 }}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{
              textTransform: "none",
              fontSize: { xs: "13px", sm: "14px" },
              borderColor: "#ddd",
              color: "#666",
              justifyContent: "flex-start",
              pl: 1,
            }}
          >
            Upload Return Video
            <input
              type="file"
              hidden
              accept="video/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setReturnModal((prev) => ({
                    ...prev,
                    video: e.target.files[0],
                  }));
                }
              }}
            />
          </Button>
          {returnModal.video && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: "#f9f9f9",
                borderRadius: "8px",
                border: "1px solid #eee",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#666", display: "block", mb: 0.5 }}
              >
                Selected Video:
              </Typography>
              <Typography
                variant="body2"
                noWrap
                title={returnModal.video.name}
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {returnModal.video.name}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: 150,
                  bgcolor: "#000",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <video
                  width="100%"
                  height="100%"
                  controls
                  src={URL.createObjectURL(returnModal.video)}
                  style={{ objectFit: "contain" }}
                />
              </Box>
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            onClick={handleCloseReturnModal}
            color="inherit"
            sx={{ fontSize: { xs: "13px", sm: "14px" } }}
          >
            Close
          </Button>
          <Button
            onClick={handleConfirmReturn}
            variant="contained"
            disabled={!returnModal.reason.trim() || !returnModal.video}
            sx={{
              backgroundColor: "#C8A054",
              color: "white",
              fontSize: { xs: "13px", sm: "14px" },
              "&:hover": { backgroundColor: "#b38841" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rate & Review Modal */}
      <ReviewModal
        open={reviewModal.open}
        orderId={reviewModal.orderId}
        productName={reviewModal.productName}
        productImage={reviewModal.productImage}
        initialRating={reviewModal.rating}
        initialReview={reviewModal.review}
        onClose={handleCloseReview}
        onSubmit={handleSubmitReview}
      />
    </Container>
  );
}

export default MyOrders;
